/**
 * Accordion Management Service
 *
 * Centralizes accordion state management with intelligent caching and Redux integration.
 * Extracts accordion logic from useAccordionState hook for improved testability and reusability.
 *
 * This service provides a centralized way to manage accordion open/close states across the application
 * with performance optimizations through caching and event-driven state management.
 */

import { logDebug, logError } from '../utils/utils';

// Types and interfaces
export interface AccordionState {
    [groupName: string]: boolean;
}

interface CachedAccordionData {
    state: AccordionState;
    timestamp: number;
}

interface AccordionChangeListener {
    (accordionState: AccordionState): void;
}

interface AccordionEvent {
    type: 'toggle' | 'open' | 'close' | 'closeAll' | 'reduxRequest';
    accordionName: string;
    newState?: boolean;
    timestamp: number;
}

/**
 * Centralized service for accordion state management with intelligent caching
 */
export class AccordionManagementService {
    private static instance: AccordionManagementService;
    private accordionState: AccordionState = {};
    private cache = new Map<string, CachedAccordionData>();
    private listeners: Set<AccordionChangeListener> = new Set();

    // Cache TTL in milliseconds (3 minutes - shorter than MaterialCascade since UI state changes more frequently)
    private readonly CACHE_TTL = 3 * 60 * 1000;
    private cacheTimestamps = new Map<string, number>();
    private cleanupTimer?: NodeJS.Timeout;

    private constructor() {
        this.setupCacheCleanup();
        logDebug('[AccordionManagementService]', 'Service initialized with caching and event system');
    }

    /**
     * Get singleton instance of AccordionManagementService
     */
    public static getInstance(): AccordionManagementService {
        if (!AccordionManagementService.instance) {
            AccordionManagementService.instance = new AccordionManagementService();
        }
        return AccordionManagementService.instance;
    }

    /**
     * Setup automatic cache cleanup timer
     */
    private setupCacheCleanup(): void {
        this.cleanupTimer = setInterval(() => {
            this.cleanupExpiredCache();
        }, this.CACHE_TTL / 2); // Clean every 1.5 minutes
    }

    /**
     * Clean up expired cache entries
     */
    private cleanupExpiredCache(): void {
        const now = Date.now();
        let cleanedCount = 0;

        // Convert to array to avoid iteration issues
        const cacheEntries = Array.from(this.cacheTimestamps.entries());
        for (const [key, timestamp] of cacheEntries) {
            if (now - timestamp > this.CACHE_TTL) {
                this.cache.delete(key);
                this.cacheTimestamps.delete(key);
                cleanedCount++;
            }
        }

        if (cleanedCount > 0) {
            logDebug('[AccordionManagementService]', `Cache cleanup completed: ${cleanedCount} entries removed, ${this.cache.size} remaining`);
        }
    }

    /**
     * Get cached accordion state if available and valid
     */
    private getCachedState(key: string): AccordionState | null {
        const cached = this.cache.get(key);
        const timestamp = this.cacheTimestamps.get(key);

        if (cached && timestamp && Date.now() - timestamp < this.CACHE_TTL) {
            logDebug('[AccordionManagementService]', `Cache hit for accordion state: ${key}`);
            return cached.state;
        }

        return null;
    }

    /**
     * Cache accordion state with timestamp
     */
    private setCachedState(key: string, state: AccordionState): void {
        const now = Date.now();
        this.cache.set(key, { state: { ...state }, timestamp: now });
        this.cacheTimestamps.set(key, now);
    }

    /**
     * Notify all listeners of accordion state changes
     */
    private notifyListeners(): void {
        this.listeners.forEach(listener => {
            try {
                listener({ ...this.accordionState });
            } catch (error) {
                logError('[AccordionManagementService]', `Error notifying listener: ${error}`);
            }
        });
    }

    /**
     * Log accordion events for debugging
     */
    private logAccordionEvent(event: AccordionEvent): void {
        logDebug('[AccordionManagementService]', `${event.type.toUpperCase()}: ${event.accordionName} -> ${event.newState}`);
    }

    /**
     * Get current accordion state
     */
    public getAccordionState(): AccordionState {
        const cacheKey = 'current_state';
        const cached = this.getCachedState(cacheKey);

        if (cached) {
            return cached;
        }

        // Cache current state
        this.setCachedState(cacheKey, this.accordionState);
        return { ...this.accordionState };
    }

    /**
     * Toggle accordion state (open <-> closed)
     * @param accordionName - Name of the accordion to toggle
     * @returns New state of the accordion (true = open, false = closed)
     */
    public toggleAccordion(accordionName: string): boolean {
        const currentState = !!this.accordionState[accordionName];
        const newState = !currentState;

        this.accordionState = {
            ...this.accordionState,
            [accordionName]: newState,
        };

        // Log the event
        this.logAccordionEvent({
            type: 'toggle',
            accordionName,
            newState,
            timestamp: Date.now(),
        });

        // Clear relevant cache entries
        this.cache.delete('current_state');

        // Notify listeners
        this.notifyListeners();

        return newState;
    }

    /**
     * Open specific accordion
     * @param accordionName - Name of the accordion to open
     */
    public openAccordion(accordionName: string): void {
        if (this.accordionState[accordionName] === true) {
            return; // Already open, no need to change
        }

        this.accordionState = {
            ...this.accordionState,
            [accordionName]: true,
        };

        this.logAccordionEvent({
            type: 'open',
            accordionName,
            newState: true,
            timestamp: Date.now(),
        });

        // Clear cache
        this.cache.delete('current_state');
        this.notifyListeners();
    }

    /**
     * Close specific accordion
     * @param accordionName - Name of the accordion to close
     */
    public closeAccordion(accordionName: string): void {
        if (this.accordionState[accordionName] === false || !(accordionName in this.accordionState)) {
            return; // Already closed or doesn't exist, no need to change
        }

        this.accordionState = {
            ...this.accordionState,
            [accordionName]: false,
        };

        this.logAccordionEvent({
            type: 'close',
            accordionName,
            newState: false,
            timestamp: Date.now(),
        });

        // Clear cache
        this.cache.delete('current_state');
        this.notifyListeners();
    }

    /**
     * Close all accordions
     */
    public closeAllAccordions(): void {
        const hasOpenAccordions = Object.values(this.accordionState).some(state => state === true);

        if (!hasOpenAccordions) {
            return; // No accordions open, no need to change
        }

        // Close all accordions
        const newState: AccordionState = {};
        Object.keys(this.accordionState).forEach(key => {
            newState[key] = false;
        });

        this.accordionState = newState;

        this.logAccordionEvent({
            type: 'closeAll',
            accordionName: 'all',
            newState: false,
            timestamp: Date.now(),
        });

        // Clear cache
        this.cache.delete('current_state');
        this.notifyListeners();
    }

    /**
     * Handle Redux accordion open request
     * Replicates the logic from useAccordionState useEffect
     * @param accordionName - Name of accordion to open (null to ignore)
     */
    public handleReduxAccordionRequest(accordionName: string | null): void {
        if (!accordionName) {
            return; // No accordion specified, ignore request
        }

        logDebug('[AccordionManagementService] Redux request to open accordion:', accordionName);

        this.openAccordion(accordionName);

        this.logAccordionEvent({
            type: 'reduxRequest',
            accordionName,
            newState: true,
            timestamp: Date.now(),
        });
    }

    /**
     * Subscribe to accordion state changes
     * @param listener - Function to call when accordion state changes
     * @returns Unsubscribe function
     */
    public subscribe(listener: AccordionChangeListener): () => void {
        this.listeners.add(listener);

        logDebug('[AccordionManagementService]', `New listener subscribed, total: ${this.listeners.size}`);

        return () => {
            this.listeners.delete(listener);
            logDebug('[AccordionManagementService]', `Listener unsubscribed, total: ${this.listeners.size}`);
        };
    }

    /**
     * Check if specific accordion is open
     * @param accordionName - Name of accordion to check
     * @returns True if accordion is open, false otherwise
     */
    public isAccordionOpen(accordionName: string): boolean {
        return !!this.accordionState[accordionName];
    }

    /**
     * Get list of all open accordions
     * @returns Array of accordion names that are currently open
     */
    public getOpenAccordions(): string[] {
        return Object.keys(this.accordionState).filter(key => this.accordionState[key] === true);
    }

    /**
     * Reset all accordion state (useful for testing or new project initialization)
     */
    public resetAllAccordions(): void {
        this.accordionState = {};
        this.cache.clear();
        this.cacheTimestamps.clear();

        this.notifyListeners();

        logDebug('[AccordionManagementService]', 'All accordion state reset');
    }

    /**
     * Get cache statistics for debugging
     */
    public getCacheStats(): { size: number; hitRate: number; oldestEntry: number | null } {
        const now = Date.now();
        let oldestTimestamp: number | null = null;

        // Convert to array to avoid iteration issues
        const timestamps = Array.from(this.cacheTimestamps.values());
        for (const timestamp of timestamps) {
            if (oldestTimestamp === null || timestamp < oldestTimestamp) {
                oldestTimestamp = timestamp;
            }
        }

        return {
            size: this.cache.size,
            hitRate: 0, // Would need to track hits/misses to calculate this
            oldestEntry: oldestTimestamp ? now - oldestTimestamp : null,
        };
    }

    /**
     * Cleanup method for service shutdown
     */
    public cleanup(): void {
        if (this.cleanupTimer) {
            clearInterval(this.cleanupTimer);
            this.cleanupTimer = undefined;
        }

        this.cache.clear();
        this.cacheTimestamps.clear();
        this.listeners.clear();

        logDebug('[AccordionManagementService]', 'Service cleanup completed');
    }
}

// Export singleton instance for backward compatibility and ease of use
export const accordionManagementService = AccordionManagementService.getInstance();

// Export default for consistent import patterns
export default AccordionManagementService;
